import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Check, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import axios from 'axios';
import { mockDataHrvatski } from '../mockHrvatski';
import { usePageTracking, trackFormSubmit, trackSignupStart } from '../hooks/useAnalytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Hrvatski = () => {
  const location = useLocation();
  
  // Track page view
  usePageTracking('/hrvatski');
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    agreedToPrivacy: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSignupModal, setShowSignupModal] = useState(false);
  const [signupFormData, setSignupFormData] = useState({
    name: '',
    email: '',
    phone: '',
    agreedToPrivacy: false
  });

  const content = mockDataHrvatski;

  // Scroll to form if navigated from Start page
  useEffect(() => {
    if (location.state?.scrollToForm) {
      setTimeout(() => {
        document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, [location]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (checked) => {
    setFormData(prev => ({ ...prev, agreedToPrivacy: checked }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.agreedToPrivacy) {
      toast.error(content.messages.privacyError);
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contacts/`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        agreed_to_privacy: formData.agreedToPrivacy,
        source: 'hrvatski'
      });

      toast.success(content.messages.submitSuccess);
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        agreedToPrivacy: false
      });
    } catch (error) {
      console.error('Form submission error:', error);
      const errorMessage = error.response?.data?.detail || content.messages.submitError;
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToForm = () => {
    document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleBookCall = () => {
    window.open('https://calendly.com/electrumprosperitygroup/precious-metals-opportunity-croatia', '_blank');
  };

  const handleJoinNow = () => {
    setShowSignupModal(true);
  };

  const handleSignupSubmit = async (e) => {
    e.preventDefault();
    
    if (!signupFormData.agreedToPrivacy) {
      toast.error('Morate prihvatiti politiku privatnosti.');
      return;
    }

    if (!signupFormData.name || !signupFormData.email) {
      toast.error('Molimo unesite svoje ime i email.');
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contacts/`, {
        name: signupFormData.name,
        email: signupFormData.email,
        phone: signupFormData.phone,
        agreed_to_privacy: signupFormData.agreedToPrivacy,
        source: 'hrvatski-signup'
      });

      const response = await axios.get(`${API}/join-links/next-rotation`);
      const joinLink = response.data.join_now_link;
      
      toast.success('Hvala! Bit ćete preusmjereni na stranicu za registraciju.');
      
      setShowSignupModal(false);
      setSignupFormData({
        name: '',
        email: '',
        phone: '',
        agreedToPrivacy: false
      });

      setTimeout(() => {
        window.open(joinLink, '_blank');
      }, 500);

    } catch (error) {
      console.error('Signup form error:', error);
      toast.error('Došlo je do greške. Molimo pokušajte ponovo.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="landing-page">
      {/* Header */}
      <header className="header">
        <div className="header-container">
          <div className="logo">
            <img src="/epg_logo.png" alt="EPG Logo" className="logo-icon" />
            <span className="logo-text">{content.header.logoText}</span>
          </div>
          <div className="header-buttons">
            <Button variant="outline" className="header-btn" onClick={handleBookCall}>
              {content.header.buttons[0].text}
            </Button>
            <Button variant="outline" className="header-btn" onClick={() => window.open('/privacy', '_blank')}>
              {content.header.buttons[1].text}
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-container">
          <div className="hero-content">
            <div className="hero-badge">{content.hero.badge}</div>
            <h1 className="hero-headline">{content.hero.headline}</h1>
            <p className="hero-subheading">{content.hero.subheading}</p>
            
            <div className="benefits-list">
              {content.hero.benefits.map((benefit, index) => (
                <div key={index} className="benefit-item">
                  <Check className="check-icon" />
                  <span>{benefit}</span>
                </div>
              ))}
            </div>

            <div className="stats-grid">
              {content.hero.stats.map((stat, index) => (
                <Card key={index} className="stat-card">
                  <CardContent className="stat-content">
                    <div className="stat-value">{stat.value}</div>
                    <div className="stat-label">{stat.label}</div>
                    {stat.sublabel && <div className="stat-sublabel">{stat.sublabel}</div>}
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="hero-ctas">
              <Button className="cta-primary" onClick={handleBookCall}>
                {content.hero.ctas[0].text}
              </Button>
              <Button className="cta-secondary" onClick={handleJoinNow}>
                {content.hero.ctas[1].text}
              </Button>
            </div>

            <div className="hero-email">
              Email: <a href={`mailto:${content.hero.email}`}>{content.hero.email}</a>
            </div>
          </div>

          <div className="hero-video">
            <div className="video-placeholder clickable-video">
              <img 
                src="/epg_logo.png" 
                alt="EPG Logo" 
                className="video-logo"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Main Videos Section */}
      <section className="main-videos-section">
        <div className="main-videos-container">
          <div className="video-item">
            <div className="video-player-large clickable-video">
              <img 
                src="/epg_logo.png" 
                alt="EPG Logo" 
                className="video-logo"
              />
            </div>
            <p className="video-title-large">{content.videos.title1}</p>
          </div>
          <div className="video-item">
            <div className="video-player-large clickable-video">
              <img 
                src="/epg_logo.png" 
                alt="EPG Logo" 
                className="video-logo"
              />
            </div>
            <p className="video-title-large">{content.videos.title2}</p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section">
        <div className="features-container">
          {content.features.map((feature, index) => (
            <Card key={index} className="feature-card">
              <CardContent className="feature-content">
                <h3 className="feature-title">{feature.title}</h3>
                <p className="feature-description">{feature.description}</p>
                {index === 2 && (
                  <Button className="feature-btn" onClick={handleBookCall}>
                    {content.header.buttons[0].text}
                  </Button>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="contact-section" id="contact-form">
        <div className="contact-container">
          <h2 className="contact-title">{content.contactForm.title}</h2>
          <form onSubmit={handleSubmit} className="contact-form">
            <Input
              type="text"
              name="name"
              placeholder={content.contactForm.fields[0].placeholder}
              value={formData.name}
              onChange={handleInputChange}
              required
              className="form-input"
              disabled={isSubmitting}
            />
            <Input
              type="email"
              name="email"
              placeholder={content.contactForm.fields[1].placeholder}
              value={formData.email}
              onChange={handleInputChange}
              required
              className="form-input"
              disabled={isSubmitting}
            />
            <Input
              type="tel"
              name="phone"
              placeholder={content.contactForm.fields[2].placeholder}
              value={formData.phone}
              onChange={handleInputChange}
              required
              className="form-input"
              disabled={isSubmitting}
            />
            <Button type="submit" className="form-submit" disabled={isSubmitting}>
              {isSubmitting ? content.messages.sending : content.contactForm.submitText}
            </Button>
            <div className="privacy-checkbox">
              <Checkbox
                id="privacy"
                checked={formData.agreedToPrivacy}
                onCheckedChange={handleCheckboxChange}
                disabled={isSubmitting}
              />
              <label htmlFor="privacy" className="privacy-label">
                {content.contactForm.privacyText}{' '}
                <a href="/privacy" target="_blank" rel="noopener noreferrer" className="privacy-link">{content.contactForm.privacyLink}</a>.
              </label>
            </div>
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-container">
          <div className="footer-cta">
            <span>{content.footer.cta}</span>
            <Button className="footer-btn" onClick={handleBookCall}>
              {content.header.buttons[0].text}
            </Button>
            <Button className="footer-btn-secondary" onClick={handleJoinNow}>
              {content.footer.ctaButton}
            </Button>
          </div>
          <div className="footer-info">
            <span>{content.footer.copyright}</span>
            <span>•</span>
            <a href={`mailto:${content.footer.email}`}>{content.footer.email}</a>
          </div>
        </div>
      </footer>

      {/* Signup Modal */}
      {showSignupModal && (
        <div className="signup-modal-overlay" onClick={() => setShowSignupModal(false)}>
          <div className="signup-modal" onClick={(e) => e.stopPropagation()}>
            <button className="signup-modal-close" onClick={() => setShowSignupModal(false)}>
              <X size={24} />
            </button>
            <h2 className="signup-modal-title">Registracija</h2>
            <p className="signup-modal-subtitle">Ispunite obrazac za nastavak na stranicu za registraciju</p>
            
            <form onSubmit={handleSignupSubmit} className="signup-modal-form">
              <div className="signup-form-field">
                <Input
                  type="text"
                  id="signup_name_hr"
                  placeholder="Puno ime *"
                  value={signupFormData.name}
                  onChange={(e) => setSignupFormData(prev => ({ ...prev, name: e.target.value }))}
                  required
                  className="signup-input"
                />
              </div>
              <div className="signup-form-field">
                <Input
                  type="email"
                  id="signup_email_hr"
                  placeholder="Email *"
                  value={signupFormData.email}
                  onChange={(e) => setSignupFormData(prev => ({ ...prev, email: e.target.value }))}
                  required
                  className="signup-input"
                />
              </div>
              <div className="signup-form-field">
                <Input
                  type="tel"
                  id="signup_phone_hr"
                  placeholder="Telefon"
                  value={signupFormData.phone}
                  onChange={(e) => setSignupFormData(prev => ({ ...prev, phone: e.target.value }))}
                  className="signup-input"
                />
              </div>
              <div className="signup-checkbox-container">
                <Checkbox
                  id="signup-privacy-hr"
                  checked={signupFormData.agreedToPrivacy}
                  onCheckedChange={(checked) => setSignupFormData(prev => ({ ...prev, agreedToPrivacy: checked }))}
                />
                <label htmlFor="signup-privacy-hr" className="signup-checkbox-label">
                  Prihvaćam <a href="/privacy" target="_blank" rel="noopener noreferrer">Politiku privatnosti</a>
                </label>
              </div>
              <Button 
                type="submit" 
                className="signup-submit-btn"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Obrada...' : 'Dalje'}
              </Button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Hrvatski;
